import Image from 'next/image'
import { classNames } from '@/lib/util'

import LogoLight from '@/assets/logo.svg'
import LogoDark from '@/assets/logo-dark.svg'
import Mark from '@/assets/icon.svg'
import MarkBlack from '@/assets/icon-black.svg'
import MarkWhite from '@/assets/icon-white.svg'

/**
 * The Fuse wordmark: the gradient mark plus the "fuse" lettering.
 * Swaps the lettering colour with the theme; the mark keeps its gradient in both.
 *
 * @param {number} props.height - Rendered height in pixels. Width follows the aspect ratio.
 * @param {string} props.className - Additional CSS class for the image.
 * @param {boolean} props.priority - Preload the asset. Use on above-the-fold logos.
 */
export function Logo({ height = 32, className, dark, priority = false }: {
  height?: number
  className?: string
  /**
   * Which lettering to draw, for a surface that does not announce its darkness
   * with the `dark` class.
   *
   * A public profile is exactly that: it carries its theme on `data-page-theme`
   * and has five of them, some light and some dark, none of which set that
   * class. Left to CSS there, the logo would use its light-background lettering
   * on every one of them, black on black included. Omit this anywhere the app's
   * own theme applies and the swap stays where it belongs, in CSS.
   */
  dark?: boolean
  priority?: boolean
}) {
  if(dark !== undefined) {
    return (
      <Image
        src={dark ? LogoDark : LogoLight}
        alt="Fuse"
        height={height}
        style={{ height, width: 'auto' }}
        className={className}
        priority={priority}
      />
    )
  }

  return (
    <>
      <Image src={LogoLight} alt="Fuse" height={height} style={{ height, width: 'auto' }} className={classNames('dark:hidden', className)} priority={priority} />
      <Image src={LogoDark} alt="Fuse" height={height} style={{ height, width: 'auto' }} className={classNames('hidden dark:block', className)} priority={priority} />
    </>
  )
}

/**
 * The Fuse mark on its own, without the lettering. Theme independent.
 *
 * @param {number} props.size - Rendered width and height in pixels.
 * @param {string} props.className - Additional CSS class for the image.
 * @param {boolean} props.priority - Preload the asset. Use on above-the-fold logos.
 */
/**
 * The mark beside the brand name as real, selectable text.
 *
 * The wordmark above is an image, and an image is not a name: a reader who does
 * not use a screen reader, and any reviewer checking that the site calls itself
 * what the OAuth consent screen calls it, sees a picture and nothing else.
 * Google's brand review reads the rendered page, so on the public pages the
 * name has to be text.
 */
export function LogoWordmark({ size = 30, className, dark }: {
  size?: number
  className?: string
  /**
   * Which lettering to draw, for a surface that does not announce its darkness
   * with the `dark` class -- the light landing page, which carries
   * `data-landing='light'`, and a public profile, which carries its theme on
   * `data-page-theme`. Omit it anywhere the app's own theme applies and the
   * swap stays in CSS.
   */
  dark?: boolean
}) {
  // The real lockup, not the mark plus a `<span>` of Gilroy.
  //
  // The lettering in logo.svg is outlined vector paths, so setting "Fuse" in a
  // typeface drew a different letterform beside the correct mark, which is what
  // made the reconstruction look subtly wrong wherever the two sat together.
  //
  // The visible name is still real text, because that is why this component
  // exists: Google's brand review reads the rendered page and an image cannot
  // prove the site calls itself Fuse. It is `sr-only` rather than absent, so a
  // reviewer and a screen reader both find the word while a sighted visitor
  // sees the artwork.
  const artwork = dark === undefined
    ? (
      <>
        <Image src={LogoLight} alt="" aria-hidden="true" height={size} style={{ height: size, width: 'auto' }} className="dark:hidden" priority />
        <Image src={LogoDark} alt="" aria-hidden="true" height={size} style={{ height: size, width: 'auto' }} className="hidden dark:block" priority />
      </>
    )
    : (
      <Image src={dark ? LogoDark : LogoLight} alt="" aria-hidden="true" height={size} style={{ height: size, width: 'auto' }} priority />
    )

  return (
    <span className={classNames('flex items-center', className)}>
      {artwork}
      <span className="sr-only">Fuse</span>
    </span>
  )
}

export function LogoMark({ size = 40, className, alt = 'Fuse', variant = 'gradient', priority = false }: {
  size?: number
  className?: string
  /**
   * Pass `''` where the name is already written beside the mark. Two things
   * saying "Fuse" is one thing to look at and two things to listen to.
   */
  alt?: string
  /**
   * Which cut of the mark to draw.
   *
   * `gradient` is the brand mark and the default, for anywhere the mark is the
   * thing being looked at. The flat cuts are for somewhere the mark is a small
   * piece of chrome on a surface of a known colour -- a badge on a page whose
   * background the owner chose, say, where a gradient competes with whatever
   * is behind it and a single ink does not.
   */
  variant?: 'gradient' | 'black' | 'white'
  priority?: boolean
}) {
  const src = variant === 'black' ? MarkBlack : variant === 'white' ? MarkWhite : Mark
  return <Image src={src} alt={alt} aria-hidden={alt === '' || undefined} width={size} height={size} className={className} priority={priority} />
}
